"use client";
import React from "react";

type Props = { children: React.ReactNode; fallback?: React.ReactNode };
type State = { hasError: boolean; error?: Error };

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info);
    if (typeof window !== "undefined") {
      // @ts-ignore
      window.__LAST_ERROR__ = error;
      // silent auto-reload up to 2 times - not noticeable, no Try again flash
      try {
        const KEY = "spanda_error_reload_count";
        const MAX = 2;
        const EXPIRY = 10 * 60 * 1000;
        const raw = window.sessionStorage.getItem(KEY);
        const data = raw ? (JSON.parse(raw) as { c: number; ts: number }) : { c: 0, ts: Date.now() };
        const expired = Date.now() - data.ts > EXPIRY;
        const count = expired ? 0 : data.c;
        if (count < MAX) {
          window.sessionStorage.setItem(KEY, JSON.stringify({ c: count + 1, ts: expired ? Date.now() : data.ts }));
          window.setTimeout(() => window.location.reload(), 100);
        }
      } catch {}
    }
  }
  render() {
    if (this.state.hasError) {
      // if still in auto-retry window, show invisible spinner (looks like loading, not error)
      try {
        const KEY = "spanda_error_reload_count";
        const MAX = 2;
        const EXPIRY = 10 * 60 * 1000;
        const raw = typeof window !== "undefined" ? window.sessionStorage.getItem(KEY) : null;
        const data = raw ? (JSON.parse(raw) as { c: number; ts: number }) : { c: 0, ts: Date.now() };
        const expired = Date.now() - data.ts > EXPIRY;
        const count = expired ? 0 : data.c;
        if (count < MAX) {
          return (
            <div className="flex min-h-[20vh] flex-col items-center justify-center p-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-saintiantblue" />
            </div>
          );
        }
      } catch {}
      return (
        this.props.fallback ?? (
          <div className="p-8 text-center">
            <h2 className="text-xl font-bold text-saintiantblue">Something went wrong</h2>
            <p className="mt-2 text-sm text-slate-600">Please refresh the page or try again later.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 rounded-full bg-saintiantblue px-6 py-2 text-white text-sm"
            >
              Try again
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
